import React, { useRef, useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import {
  withStyles,
  Typography,
  Popover,
  CircularProgress,
  Tooltip,
  FormControl,
} from '@material-ui/core'
import { ChromePicker } from 'react-color'
import { useDispatch } from 'react-redux'
import { messageBoxHideAction } from 'src/reducers/message-box/action'
import styles from './styles'
import {
  CoreToggleButtonGroup,
  CoreToggleButton,
  CoreButton,
  CoreIconButton,
  CoreTextField,
  CoreRadioButton,
} from 'src/components/core'

import {
  handleTextColorChange,
  handleBackgroundColorChange,
  handleTabTitleChange,
  handleIconSelect,
  handleIconDelete,
  showIconSelector,
  deleteTabHeader,
  getInitialTabHeaderState,
} from '../utils/tabHeaderUtils'

const TabHeaderOptionsModal = props => {
  const { classes, editor, target, selection, position } = props
  const dispatch = useDispatch()

  const [title, setTitle] = useState('')
  const [textColor, setTextColor] = useState('')
  const [backgroundColor, setBackgroundColor] = useState('')
  const [selectedIcon, setSelectedIcon] = useState('')
  const [selectedIconName, setSelectedIconName] = useState(
    'lets-icons:add-round',
  )
  // Add missing state variables
  const [tabIndex, setTabIndex] = useState('1')
  const [isActive, setIsActive] = useState('false')
  const [shouldShowDeleteButton, setShouldShowDeleteButton] = useState(true)

  const [anchorEl, setAnchorEl] = useState(null)
  const [backgroundColorAnchorEl, setBackgroundColorAnchorEl] = useState(null)
  const [pickerVisibles, setPickerVisibles] = useState({
    textColor: false,
    backgroundColor: false,
  })

  useEffect(() => {
    const initialState = getInitialTabHeaderState(selection)
    setSelectedIcon(initialState.selectedIcon)
    setSelectedIconName(initialState.selectedIconName)
    setTitle(initialState.title)
    setTextColor(initialState.textColor)
    setBackgroundColor(initialState.backgroundColor)
    setTabIndex(initialState.tabIndex)
    setIsActive(initialState.isActive)

    // Check if delete button should be shown
    const parent = selection.parent
    if (parent) {
      const tabHeaders = Array.from(parent.getChildren()).filter(
        child => child.name === 'tabHeader',
      )
      setShouldShowDeleteButton(tabHeaders.length > 1)
    }
  }, [selection])

  const onTitleChange = event => {
    handleTabTitleChange(event, {
      editor,
      position,
      textColor,
      backgroundColor,
      selectedIcon,
      selectedIconName,
      setTitle,
      tabIndex,
      isActive,
    })
  }

  const onTextColorChange = event => {
    handleTextColorChange(event, {
      editor,
      position,
      title,
      backgroundColor,
      selectedIcon,
      selectedIconName,
      setTextColor,
      tabIndex,
      isActive,
    })
  }

  const onBackgroundColorChange = event => {
    handleBackgroundColorChange(event, {
      editor,
      position,
      title,
      textColor,
      selectedIcon,
      selectedIconName,
      setBackgroundColor,
      tabIndex,
      isActive,
    })
  }

  const onIconSelect = icon => {
    handleIconSelect(icon, {
      editor,
      position,
      title,
      textColor,
      backgroundColor,
      setSelectedIcon,
      setSelectedIconName,
      tabIndex,
      isActive,
    })
  }

  const onIconDelete = () => {
    handleIconDelete({
      editor,
      position,
      title,
      textColor,
      backgroundColor,
      setSelectedIcon,
      setSelectedIconName,
      tabIndex,
      isActive,
    })
  }

  const onShowIconSelector = () => {
    showIconSelector({
      editor,
      target,
      handleIconSelect: onIconSelect,
      dispatch,
    })
  }

  const onDeleteClick = () => {
    deleteTabHeader(editor, position)
    dispatch(messageBoxHideAction())
  }

  return (
    <div className={classes.box}>
      <div className={classes.row}>
        <div>
          <Typography className={classes.header}>Tab Title</Typography>
        </div>
        <div className={classes.textbox}>
          <CoreTextField
            className={classes.textfield}
            fullWidth
            variant="outlined"
            color="primary"
            size="small"
            value={title}
            onChange={onTitleChange}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div>
          <Typography className={classes.header}>Tab Style</Typography>
        </div>

        {/* Colors Column */}
        <div className={classes.column}>
          {/* Background Color Section */}
          <div className={classes.colorDiv}>
            <Typography className={classes.message}>Tab Color</Typography>
            <div className={classes.boxIconButton}>
              <div className={classes.inputItem}>
                <div className={classes.pickerBg}>
                  <span
                    className={classes.pickerSpan}
                    style={{
                      backgroundColor: backgroundColor,
                      zIndex: '999999 !important',
                    }}
                    onClick={event => {
                      setPickerVisibles({
                        ...pickerVisibles,
                        backgroundColor: !pickerVisibles.backgroundColor,
                      })
                      setBackgroundColorAnchorEl(event.currentTarget)
                    }}
                  ></span>
                  <Popover
                    className={classes.popover}
                    id={
                      pickerVisibles.backgroundColor
                        ? 'backgroundColor'
                        : undefined
                    }
                    open={pickerVisibles.backgroundColor}
                    anchorEl={backgroundColorAnchorEl}
                    onClose={() => {
                      setPickerVisibles({
                        ...pickerVisibles,
                        backgroundColor: false,
                      })
                      setBackgroundColorAnchorEl(null)
                    }}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <ChromePicker
                      color={backgroundColor}
                      onChange={onBackgroundColorChange}
                    />
                  </Popover>
                </div>
              </div>
              <Typography className={classes.header}>
                {backgroundColor}
              </Typography>
            </div>
          </div>

          {/* Text Color Section */}
          <div className={classes.colorDiv}>
            <Typography className={classes.message}>Text Color</Typography>
            <div className={classes.boxIconButton}>
              <div className={classes.inputItem}>
                <div className={classes.pickerBg}>
                  <span
                    className={classes.pickerSpan}
                    style={{
                      backgroundColor: textColor,
                      zIndex: '999999 !important',
                    }}
                    onClick={event => {
                      setPickerVisibles({
                        ...pickerVisibles,
                        textColor: !pickerVisibles.textColor,
                      })
                      setAnchorEl(event.currentTarget)
                    }}
                  ></span>
                  <Popover
                    className={classes.popover}
                    id={pickerVisibles.textColor ? 'textColor' : undefined}
                    open={pickerVisibles.textColor}
                    anchorEl={anchorEl}
                    onClose={() => {
                      setPickerVisibles({ ...pickerVisibles, textColor: false })
                      setAnchorEl(null)
                    }}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <ChromePicker
                      color={textColor}
                      onChange={onTextColorChange}
                    />
                  </Popover>
                </div>
              </div>
              <Typography className={classes.header}>{textColor}</Typography>
            </div>
          </div>
        </div>
      </div>

      {/* Conditionally render delete button row */}
      {shouldShowDeleteButton && (
        <div className={classes.row}>
          <div className={classes.boxDelete}>
            <CoreButton
              variant="text"
              color="error"
              className={classes.deleteButton}
              size="small"
              onClick={onDeleteClick}
            >
              <Icon icon="iconoir:trash" />
              <Typography className={classes.deleteTitle}>Delete</Typography>
            </CoreButton>
          </div>
        </div>
      )}
    </div>
  )
}

export default withStyles(styles)(TabHeaderOptionsModal)
